import { createHash } from 'crypto';
import { CoachClientSlot, CoachClientStatus, isSlotSleeping } from './coach';
import { CouchUserInfo, SubscriptionStatus } from './couch-user';
import { isAccessGrantingStatus } from './license-reconciler';

// What one coach's history looks like (#6767, Phase 2 of #4456).
//
// The server writes one row to the server-only database `log_coach_history`
// whenever something about a coach changes: a client joins, is removed, leaves,
// falls asleep or wakes; the coaching subscription changes; or a charge arrives.
// Every row stores the state AFTER the change, so each row stands on its own and
// a chart can be drawn from any subset. Rows name a client only by its slot id,
// never by account id or name. The rules in plain words, and why each one is so:
// design-docs/coach-billing-analytics.md, "Phase 2 — recorded history".

/** The server-only database holding every coach's history rows. */
export const kCoachHistoryDb = 'log_coach_history';

/** Bumped when a row's shape changes in a way a reader must know about. */
export const COACH_HISTORY_SCHEMA_VERSION = 1 as const;

/** How the coach pays at the moment a row was written. For a store the route
 *  IS the provider, so a row has no separate provider field. */
export enum CoachHistoryRoute {
  /** Paddle per-seat web subscription (`coachSeatData`). */
  Paddle = 'paddle',
  /** Apple fixed-tier plan (`coachTierData`). */
  Apple = 'apple',
  /** Google Play fixed-tier plan (`coachTierData`). */
  GooglePlay = 'googlePlay',
  /** A coach still carrying the retired PayPro block (`coachBillingData`). */
  Metered = 'metered',
  /** Slots set by hand (`coachMaxSlots`), no payment. */
  Legacy = 'legacy',
  /** No coach block at all. */
  None = 'none',
}

/** What a history row records. */
export enum CoachHistoryKind {
  ClientJoined = 'client-joined',
  /** The coach removed the client. */
  ClientRemoved = 'client-removed',
  /** The client left on their own. */
  ClientLeft = 'client-left',
  ClientSlept = 'client-slept',
  ClientWoke = 'client-woke',
  SubscriptionChanged = 'subscription-changed',
  Charge = 'charge',
}

/** The kinds that are about one client slot. */
export type CoachHistoryClientKind =
  | CoachHistoryKind.ClientJoined
  | CoachHistoryKind.ClientRemoved
  | CoachHistoryKind.ClientLeft
  | CoachHistoryKind.ClientSlept
  | CoachHistoryKind.ClientWoke;

/** The route money was actually paid on. Not the same as a row's `route`: a
 *  coach moving from a store plan to Paddle can still have `route: apple` while
 *  the store plan runs out, and their Paddle charge is still paid on Paddle. */
export type CoachChargePaymentRoute = CoachHistoryRoute.Paddle | CoachHistoryRoute.Apple | CoachHistoryRoute.GooglePlay;

/** Where a charge's amount came from, so nobody mistakes an estimate for an
 *  exact charge. Google's figure is the subscription's CURRENT price, which can
 *  differ from what was actually charged. */
export type CoachChargeAmountSource = 'paddle-grand-total' | 'apple-transaction-price' | 'google-current-price';

/**
 * The handful of subscription fields compared to decide whether a coach's
 * subscription "changed". Built from stored fields only, never from the current
 * time, or a projection would "change" with nothing written.
 */
export interface CoachSubscriptionProjection {
  route: CoachHistoryRoute;
  /** The paying block's status. `active` for `metered` and `legacy`, which pay
   *  nothing through us; `none` for `none`. */
  status: SubscriptionStatus;
  /** Paddle: the seat count. */
  seats?: number;
  /** Stores: the plan's internal id. */
  tierId?: string;
  /** Stores: the plan's client limit. */
  tierCap?: number;
  /** Legacy: the hand-set slot count. */
  coachMaxSlots?: number;
  /** Paddle and stores: whether the subscription is set to end at the end of
   *  its period. Absent on the other routes. */
  cancelScheduled?: boolean;
}

/** The projection plus what reading it found wrong, so the caller can report
 *  damage it repaired rather than hide it. */
export interface CoachSubscriptionReading {
  projection: CoachSubscriptionProjection;
  /** Whether the block the projection was taken from is marked as a test
   *  purchase (a Paddle sandbox seat or a store test purchase). */
  isTestPurchase: boolean;
  /** The fields that held a value of the wrong type or an unknown value, named
   *  like `coachSeatData.quantity`. Each was read as absent. */
  damagedFields: string[];
}

/** Fields every row carries: who, when, what, and the state after the change. */
export interface CoachHistoryRowBase extends CoachSubscriptionProjection {
  _id: string;
  _rev?: string;
  schemaVersion: typeof COACH_HISTORY_SCHEMA_VERSION;
  coachUid: string;
  /** When the change happened, ISO 8601 in UTC. */
  at_utc: string;
  kind: CoachHistoryKind;
  /** Claimed clients who are awake, after the change. */
  clientsAwake: number;
  /** Claimed clients who are asleep, after the change. */
  clientsAsleep: number;
  isTestPurchase: boolean;
}

export interface CoachHistoryClientRow extends CoachHistoryRowBase {
  kind: CoachHistoryClientKind;
  /** The client's slot id (`coach-client/<uuid>`). Never the client's account id. */
  slotId: string;
  /** Sleep and wake only: who did it. */
  by?: 'coach' | 'auto';
}

export interface CoachHistorySubscriptionRow extends CoachHistoryRowBase {
  kind: CoachHistoryKind.SubscriptionChanged;
  /** The projection before the change; absent when the coach had no coach
   *  subscription before (a start). */
  before?: CoachSubscriptionProjection;
  /** A short stable name of the site that wrote the change, for example
   *  `paddle-webhook` or `seat-sync`. */
  source: string;
}

export interface CoachHistoryChargeRow extends CoachHistoryRowBase {
  kind: CoachHistoryKind.Charge;
  /** The provider's id of the charge: Paddle's transaction id, Apple's
   *  `transactionId`, Google's `orderId`. */
  chargeId: string;
  paymentRoute: CoachChargePaymentRoute;
  /** In major units (29.97, not 2997). */
  amount: number;
  /** ISO 4217 code. */
  currency: string;
  amountSource: CoachChargeAmountSource;
  /** Paddle: the seats this charge paid for. */
  paidSeats?: number;
  /** Stores: the plan this charge paid for. */
  paidTierId?: string;
  paidTierCap?: number;
}

export type CoachHistoryRow = CoachHistoryClientRow | CoachHistorySubscriptionRow | CoachHistoryChargeRow;

/**
 * The `_id` of a charge row: `charge-` plus the hex SHA-256 of
 * `<coachUid>:<paymentRoute>:<chargeId>`.
 *
 * Deterministic, so a redelivered charge gets a conflict and is recorded once.
 * Opaque, because CouchDB keeps a deleted document's id forever: an id holding
 * the coach's uid would leave their timeline readable after their account is
 * deleted. Keyed on the route the money was paid on, never the row's `route`.
 */
export function coachHistoryChargeId(coachUid: string, paymentRoute: CoachChargePaymentRoute, chargeId: string): string {
  const hash = createHash('sha256').update(`${coachUid}:${paymentRoute}:${chargeId}`).digest('hex');
  return `charge-${hash}`;
}

/** Claimed clients split into awake and asleep. Pending invites are not
 *  clients. `classifyCoachSlotsForCap` skips asleep slots, so it cannot give the
 *  asleep count. */
export function countCoachClientsForHistory(
  slots: readonly Pick<CoachClientSlot, 'status' | 'sleepState'>[],
): { clientsAwake: number; clientsAsleep: number } {
  let clientsAwake = 0;
  let clientsAsleep = 0;
  // for each claimed slot: count it as asleep or awake
  for (const slot of slots) {
    if (slot?.status !== CoachClientStatus.Claimed) {
      continue;
    }
    if (isSlotSleeping(slot)) {
      clientsAsleep++;
    } else {
      clientsAwake++;
    }
  }
  return { clientsAwake, clientsAsleep };
}

const kKnownStatuses = new Set<string>(Object.values(SubscriptionStatus));

/** A status as stored, or `none` when it is missing or unknown. */
function readStatus(value: unknown, field: string, damaged: string[]): SubscriptionStatus {
  if (typeof value === 'string' && kKnownStatuses.has(value)) {
    return value as SubscriptionStatus;
  }
  if (value !== undefined) {
    damaged.push(field);
  }
  return SubscriptionStatus.None;
}

/** A whole number of at least 0, or undefined when it is missing or not one. */
function readCount(value: unknown, field: string, damaged: string[]): number | undefined {
  if (typeof value === 'number' && Number.isInteger(value) && value >= 0) {
    return value;
  }
  if (value !== undefined && value !== null) {
    damaged.push(field);
  }
  return undefined;
}

/** A non-empty string, or undefined. */
function readText(value: unknown, field: string, damaged: string[]): string | undefined {
  if (typeof value === 'string' && value.length > 0) {
    return value;
  }
  if (value !== undefined && value !== null && value !== '') {
    damaged.push(field);
  }
  return undefined;
}

/** A block read as an object, or undefined when it is absent or not an object. */
function readBlock(value: unknown, field: string, damaged: string[]): Record<string, unknown> | undefined {
  if (value === undefined || value === null) {
    return undefined;
  }
  if (typeof value !== 'object' || Array.isArray(value)) {
    damaged.push(field);
    return undefined;
  }
  return value as Record<string, unknown>;
}

/** Whether a block says it is a test purchase. Only an explicit `true` counts. */
function isTestBlock(block: Record<string, unknown> | undefined): boolean {
  return block?.isTestPurchase === true;
}

/** The projection of a store plan block (`coachTierData`). */
function tierProjection(tier: Record<string, unknown>, route: CoachHistoryRoute, damaged: string[]): CoachSubscriptionProjection {
  const projection: CoachSubscriptionProjection = {
    route,
    status: readStatus(tier.subscriptionStatus, 'coachTierData.subscriptionStatus', damaged),
    cancelScheduled: tier.autoRenewStatus === false,
  };
  const tierId = readText(tier.tierId, 'coachTierData.tierId', damaged);
  if (tierId !== undefined) projection.tierId = tierId;
  const tierCap = readCount(tier.tierCap, 'coachTierData.tierCap', damaged);
  if (tierCap !== undefined) projection.tierCap = tierCap;
  return projection;
}

/** The projection of a Paddle seat block (`coachSeatData`). */
function seatProjection(seat: Record<string, unknown>, damaged: string[]): CoachSubscriptionProjection {
  const scheduled = seat.scheduledChange;
  const scheduledAction = typeof scheduled === 'object' && scheduled !== null ? (scheduled as { action?: unknown }).action : undefined;
  const projection: CoachSubscriptionProjection = {
    route: CoachHistoryRoute.Paddle,
    status: readStatus(seat.subscriptionStatus, 'coachSeatData.subscriptionStatus', damaged),
    cancelScheduled: scheduledAction === 'cancel',
  };
  const seats = readCount(seat.quantity, 'coachSeatData.quantity', damaged);
  if (seats !== undefined) projection.seats = seats;
  return projection;
}

/** The store a tier block belongs to, or undefined when its provider is unknown. */
function tierRoute(tier: Record<string, unknown>, damaged: string[]): CoachHistoryRoute | undefined {
  if (tier.provider === 'apple') return CoachHistoryRoute.Apple;
  if (tier.provider === 'googlePlay') return CoachHistoryRoute.GooglePlay;
  damaged.push('coachTierData.provider');
  return undefined;
}

/**
 * Which route a coach is on and the fields compared for a change, from the
 * stored fields only. The order matches the server's own coach rule
 * (`resolveCoachCapability`: tier, seat, Metered, Legacy) without its expiry
 * check:
 *
 *  1. a tier block whose status grants access → its store;
 *  2. else a seat block whose status grants access → `paddle`;
 *  3. else a `coachBillingData` block (no status check) → `metered`;
 *  4. else `coachMaxSlots > 0` → `legacy`;
 *  5. else whichever block exists, tier before seat, with its own status;
 *  6. else `none`.
 *
 * A damaged value is read as absent and named in `damagedFields`, never trusted.
 * A tier block with an unknown provider cannot name a route, so it is skipped.
 */
export function readCoachSubscription(userInfo: Partial<CouchUserInfo> | null | undefined): CoachSubscriptionReading {
  const damagedFields: string[] = [];
  const doc = (userInfo ?? {}) as Record<string, unknown>;
  const tier = readBlock(doc.coachTierData, 'coachTierData', damagedFields);
  const seat = readBlock(doc.coachSeatData, 'coachSeatData', damagedFields);
  const route = tier ? tierRoute(tier, damagedFields) : undefined;
  const usableTier = tier && route ? tier : undefined;
  const tierStatus = usableTier ? usableTier.subscriptionStatus as SubscriptionStatus : undefined;
  const seatStatus = seat ? seat.subscriptionStatus as SubscriptionStatus : undefined;

  // if a block grants access, it is the one the coach pays on (tier first)
  if (usableTier && isAccessGrantingStatus(tierStatus)) {
    return { projection: tierProjection(usableTier, route!, damagedFields), isTestPurchase: isTestBlock(usableTier), damagedFields };
  }
  if (seat && isAccessGrantingStatus(seatStatus)) {
    return { projection: seatProjection(seat, damagedFields), isTestPurchase: isTestBlock(seat), damagedFields };
  }
  // older coaches pay nothing through us, so they are always active
  if (doc.coachBillingData !== undefined && doc.coachBillingData !== null) {
    return { projection: { route: CoachHistoryRoute.Metered, status: SubscriptionStatus.Active }, isTestPurchase: false, damagedFields };
  }
  const maxSlots = readCount(doc.coachMaxSlots, 'coachMaxSlots', damagedFields);
  if (maxSlots !== undefined && maxSlots > 0) {
    return {
      projection: { route: CoachHistoryRoute.Legacy, status: SubscriptionStatus.Active, coachMaxSlots: maxSlots },
      isTestPurchase: false,
      damagedFields,
    };
  }
  // if no block grants access, report the one that exists with its own status
  if (usableTier) {
    return { projection: tierProjection(usableTier, route!, damagedFields), isTestPurchase: isTestBlock(usableTier), damagedFields };
  }
  if (seat) {
    return { projection: seatProjection(seat, damagedFields), isTestPurchase: isTestBlock(seat), damagedFields };
  }
  return { projection: { route: CoachHistoryRoute.None, status: SubscriptionStatus.None }, isTestPurchase: false, damagedFields };
}

/** Whether two projections describe the same subscription. Every field is
 *  compared; an absent field equals an absent field. */
export function coachSubscriptionProjectionsEqual(a: CoachSubscriptionProjection, b: CoachSubscriptionProjection): boolean {
  return a.route === b.route
    && a.status === b.status
    && a.seats === b.seats
    && a.tierId === b.tierId
    && a.tierCap === b.tierCap
    && a.coachMaxSlots === b.coachMaxSlots
    && a.cancelScheduled === b.cancelScheduled;
}

/** Whether a projection says the account holds no coach subscription at all. */
export function isNoCoachSubscription(projection: CoachSubscriptionProjection): boolean {
  return projection.route === CoachHistoryRoute.None;
}
